import React, { useState } from 'react';
import { Card, CardContent, TextField, Button, Box, Typography, CircularProgress } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useNotify, useRedirect } from 'react-admin';
import { usersProxy } from './apiGateway';

const resolveApiErrorStatus = (err: unknown) => {
    if (typeof err === 'object' && err && 'status' in err) {
        const status = (err as { status?: unknown }).status;
        if (typeof status === 'number') {
            return status;
        }
    }

    if (err instanceof Error) {
        const match = err.message.match(/API Error: (\d+)/);
        if (match) {
            return Number(match[1]);
        }
    }

    return null;
};

export const UserAdsSearchPage: React.FC = () => {
    const [searchValue, setSearchValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const notify = useNotify();
    const redirect = useRedirect();

    const handleSearch = async () => {
        const trimmedValue = searchValue.trim();
        if (!trimmedValue) {
            notify('Введите ID объявления или серийный номер', { type: 'warning' });
            return;
        }

        setIsLoading(true);

        try {
            const adInfo = await usersProxy.getAdInfo(trimmedValue);
            if (!adInfo?.id) {
                throw new Error('Ad not found');
            }
            redirect('show', 'userAds', trimmedValue);
        } catch (err) {
            const status = resolveApiErrorStatus(err);
            if (status === 404) {
                notify('Не найдено такое объявление', { type: 'warning' });
            } else {
                notify('Ошибка при поиске объявления', { type: 'error' });
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyPress = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            handleSearch();
        }
    };

    return (
        <Box sx={{ p: 3, maxWidth: 1200 }}>
            <Card>
                <CardContent>
                    <Typography variant="h5" component="h1" gutterBottom>
                        Поиск объявления
                    </Typography>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Введите ID объявления (ObjectId) или его серийный номер
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <TextField
                            fullWidth
                            label="ID объявления или серийный номер"
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            onKeyPress={handleKeyPress}
                            disabled={isLoading}
                            placeholder="Например: 686934dff01db5688b548791 или 1751725741"
                            variant="outlined"
                        />

                        <Button
                            variant="contained"
                            onClick={handleSearch}
                            disabled={isLoading || !searchValue.trim()}
                            startIcon={isLoading ? <CircularProgress size={20} /> : <SearchIcon />}
                            sx={{ minWidth: 120, height: 36 }}
                        >
                            {isLoading ? 'Поиск...' : 'Найти'}
                        </Button>
                    </Box>

                    <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                        • Серийный номер: числовое значение (например: 1771170584)<br />
                        • Идентификатор: ObjectId (например: 6991eb1819d8490eeda3cfac)
                    </Typography>
                </CardContent>
            </Card>
        </Box>
    );
};
